/**
 * Printing and ink cost. MASTER_PROMPT section 8.5.
 *
 * Defect D3: the legacy workbook priced the same month two ways and differed
 * by ₹86,450. There is one rate master behind this board, so there is one
 * cost — the per-machine and per-day splits are shown precisely because they
 * must add back to the same total.
 *
 * The reducer-to-ink ratio runs at about 2.2. Drift is a control signal, and
 * the direction of the drift is what tells a supervisor what to do about it.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { InkBoard } from "../../lib/types";
import { fmtDate, fmtQty, toNumber } from "../../lib/format";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";
import { BoardPage, DailyBars, Exceptions, KpiCard, day } from "./board";
import { comparisonLabel } from "../../lib/period";
import { useCompare, useReportPeriod } from "../../lib/reportPeriod";

function rupees(value: string | null, dp = 2): string {
  if (value === null) return "—";
  return `₹${fmtQty(value, dp)}`;
}

/** Ratios print to a fixed width so a column of them lines up and 2.0 stays
 *  2.0 rather than collapsing to 2 on the way through JSON. */
function ratio(value: string | null, dp = 3): string {
  return value === null ? "—" : Number(value).toFixed(dp);
}

type PressRow = InkBoard["by_machine"][number];
type BandRow = InkBoard["out_of_band"][number];

const PRESS_COLUMNS: SheetColumn<PressRow>[] = [
  // The daily sheet writes "JP" where the plant has two JP presses, so some
  // rows carry no machine. Blank rather than a name is the honest answer.
  { key: "press", label: "Press", value: (r) => r.machine },
  { key: "bags", label: "Bags", type: "number", numeric: true,
    value: (r) => toNumber(r.bags), format: (r) => fmtQty(r.bags, 0) },
  { key: "cost", label: "Cost", type: "number", numeric: true,
    value: (r) => toNumber(r.cost), format: (r) => rupees(r.cost, 0) },
  { key: "per_bag", label: "Per bag", type: "number", numeric: true,
    value: (r) => (r.cost_per_bag === null ? null : toNumber(r.cost_per_bag)),
    format: (r) => rupees(r.cost_per_bag, 4) },
  { key: "ratio", label: "Ratio", type: "number", numeric: true,
    value: (r) => (r.ratio === null ? null : toNumber(r.ratio)),
    format: (r) => ratio(r.ratio) },
];

const BAND_COLUMNS: SheetColumn<BandRow>[] = [
  { key: "date", label: "Date", type: "date", value: (r) => r.business_date,
    format: (r) => fmtDate(r.business_date) },
  { key: "press", label: "Press", value: (r) => r.machine },
  { key: "ratio", label: "Ratio", type: "number", numeric: true,
    value: (r) => toNumber(r.ratio), format: (r) => ratio(r.ratio) },
  { key: "direction", label: "Direction", value: (r) => r.direction },
];

export function Ink() {
  const [period, setPeriod] = useReportPeriod();
  const [compare] = useCompare();
  const [board, setBoard] = useState<InkBoard | null>(null);
  const presses = useSheet(board?.by_machine ?? [], PRESS_COLUMNS);
  const outOfBand = useSheet(board?.out_of_band ?? [], BAND_COLUMNS);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setBoard(null);
    setError(null);
    api
      .inkBoard(period, compare)
      .then(setBoard)
      .catch((e: ApiError) => setError(e.message));
  }, [period, compare]);

  useEffect(load, [load]);

  const band = board
    ? { lo: Number(board.ratio_band[0]), hi: Number(board.ratio_band[1]) }
    : undefined;

  return (
    <BoardPage
      title="Printing and ink"
      subtitle="One rate master, one cost — and whether the mix is holding"
      period={period}
      onPeriod={setPeriod}
      shifts
      loading={!board}
      error={error}
    >
      {board && (
        <>
          <div className="kpirow">
            {board.kpis.map((k) => (
              <KpiCard key={k.key} kpi={k} vs={comparisonLabel(period, compare)} />
            ))}
          </div>

          <Exceptions items={board.exceptions} />

          {board.entries === 0 ? (
            <div className="state state--empty">
              <span className="state__glyph" aria-hidden="true">
                ◐
              </span>
              <h3>No printing in this period</h3>
              <p>
                Ink cost appears here as soon as a print run is recorded. With
                no ink issued there is no ratio to report — the board says so
                rather than showing a zero.
              </p>
            </div>
          ) : (
            <>
              <section className="card">
                <h3 className="section-title">
                  Reducer to ink, day by day
                </h3>
                <p className="section-note">
                  Target {ratio(board.ratio_target, 1)}, acceptable between{" "}
                  {ratio(board.ratio_band[0], 1)} and{" "}
                  {ratio(board.ratio_band[1], 1)} — the shaded lane. Columns
                  outside it are marked.
                </p>
                <DailyBars
                  band={band}
                  rows={board.daily.map((d) => ({
                    date: d.business_date,
                    value: d.ratio === null ? null : Number(d.ratio),
                    title: `${day(d.business_date)} · ratio ${ratio(
                      d.ratio
                    )} · ${fmtQty(d.bags, 0)} bags · ${rupees(d.cost, 0)}`,
                  }))}
                />
                <p className="page__note">
                  Month as a whole: {ratio(board.ratio)} from{" "}
                  {fmtQty(board.ink_kg, 0)} kg ink and{" "}
                  {fmtQty(board.reducer_kg, 0)} kg reducer.
                </p>
              </section>

              <div className="board2">
                <section className="card">
                  <h3 className="section-title">By press</h3>
                  <p className="section-note">
                    A drifting press is visible here rather than averaged away
                  </p>
                  <SheetToolbar sheet={presses} exportName="ink-by-press" />
                  <div className="tablewrap">
                    <table className="table">
                      <SheetHead sheet={presses} />
                      <tbody>
                        {presses.rows.map((m) => {
                          const out =
                            m.ratio !== null &&
                            band !== undefined &&
                            (Number(m.ratio) < band.lo ||
                              Number(m.ratio) > band.hi);
                          return (
                            <tr
                              key={m.machine}
                              className={out ? "row--warn" : ""}
                            >
                              <td>{m.machine}</td>
                              <td className="num mono">{fmtQty(m.bags, 0)}</td>
                              <td className="num mono">
                                {rupees(m.cost, 0)}
                              </td>
                              <td className="num mono">
                                {rupees(m.cost_per_bag, 4)}
                              </td>
                              <td
                                className={`num mono${out ? " cell--negative" : ""}`}
                              >
                                {ratio(m.ratio)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>Total</td>
                          <td className="num mono">{fmtQty(board.bags, 0)}</td>
                          <td className="num mono">
                            <strong>{rupees(board.total_cost, 0)}</strong>
                          </td>
                          <td className="num mono">
                            {rupees(board.cost_per_bag, 4)}
                          </td>
                          <td className="num mono">{ratio(board.ratio)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <p className="page__note">{board.note}</p>
                </section>

                <section className="card">
                  <h3 className="section-title">Days outside the band</h3>
                  <p className="section-note">
                    Which way it went — a thin mix needs reducer held back, a
                    thick one needs more
                  </p>
                  {board.out_of_band.length === 0 ? (
                    <p className="section-note">
                      Every day in this period ran inside the band.
                    </p>
                  ) : (
                    <>
                    <SheetToolbar sheet={outOfBand} exportName="ink-out-of-band" />
                    <div className="tablewrap">
                      <table className="table">
                        <SheetHead sheet={outOfBand} />
                        <tbody>
                          {outOfBand.rows.map((e) => (
                            <tr
                              key={`${e.business_date}-${e.machine}`}
                              className="row--warn"
                            >
                              <td>{fmtDate(e.business_date)}</td>
                              <td>{e.machine}</td>
                              <td className="num mono cell--negative">
                                {ratio(e.ratio)}
                              </td>
                              <td>
                                <span
                                  className={`pill pill--${
                                    e.direction === "HIGH"
                                      ? "warning"
                                      : "negative"
                                  }`}
                                >
                                  {/* Reducer is a solvent: more of it thins
                                      the ink, so a high ratio runs thin. */}
                                  {e.direction === "HIGH"
                                    ? "↑ too thin"
                                    : "↓ too thick"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  )}
                </section>
              </div>
            </>
          )}
        </>
      )}
    </BoardPage>
  );
}
